import React, { ChangeEvent, Component, MouseEvent } from 'react';
import { connect, FormikProps } from 'formik';
import { compose } from 'recompose';
import { Trans } from 'react-i18next';
import { Grid, IconButton, Paper, Typography } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import MoreHorzIcon from '@material-ui/icons/MoreHoriz';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import moment from 'moment';

import { ShareType } from 'api';
import { DatePickers, ValidatedSwitch } from 'components';
import { IShare } from '../types';

interface IProps {
  suggestedTime: number;
  scheduledTime: number;
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles> {
  formik: FormikProps<IShare>;
}

interface IState {
  anchorEl: HTMLElement;
  scheduledTime: number;
  userScheduled: boolean;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: '12px 16px',
      marginBottom: theme.spacing(5),
    },
    accessIcon: {
      fontSize: theme.typography.pxToRem(32),
      marginRight: 12,
      verticalAlign: 'middle',
    },
    text: {
      marginLeft: theme.spacing(5),
    },
    error: {
      color: theme.custom.palette.red,
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(connect, withStyles(styles));

class Scheduler extends Component<IPropsEnhanced> {
  state: IState = {
    anchorEl: null,
    scheduledTime: this.props.scheduledTime,
    userScheduled: false,
  };

  getTime() {
    const { suggestedTime } = this.props;
    const { scheduledTime } = this.state;

    return scheduledTime || suggestedTime;
  }

  handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  handleCheckedValue = (shareType: ShareType) => {
    return shareType !== ShareType.Now;
  };

  handleScheduleChange = (event: ChangeEvent<HTMLInputElement>, scheduled: boolean) => {
    const { formik } = this.props;

    const { setFieldValue } = formik;
    const shareType = scheduled ? ShareType.Scheduled : ShareType.Now;
    const time = this.getTime();

    setFieldValue('share_type', shareType);

    if (scheduled) {
      setFieldValue('share_type', ShareType.Scheduled);
      setFieldValue('share_at', time);
      this.setState({ scheduledTime: time }, this.handleClose);
    }
  };

  handleSave = (time: number) => {
    const { formik } = this.props;
    const { setFieldValue } = formik;

    setFieldValue('share_type', ShareType.Scheduled);
    setFieldValue('share_at', time);

    this.setState({ scheduledTime: time, userScheduled: true }, this.handleClose);
  };

  renderSecondary() {
    const { classes, formik } = this.props;
    const { userScheduled } = this.state;

    if (!userScheduled) {
      return (
        <Typography className={classes.text}>
          <em>
            <Trans i18nKey="sharing:scheduledNote" />
          </em>
        </Typography>
      );
    }

    const time = this.getTime();
    const formattedTime = moment.unix(time).format('dddd, MMMM Do, YYYY [at] h:mma');

    const { errors } = formik;
    const hasError = !!errors.share_at;

    return (
      <Typography className={classes.text}>
        <Trans i18nKey={'sharing:scheduled'} />{' '}
        <span className={hasError ? classes.error : ''}>{formattedTime}</span>
      </Typography>
    );
  }

  render() {
    const { classes, formik } = this.props;
    const { anchorEl } = this.state;
    const { values } = formik;

    const time = this.getTime();
    const isScheduled = values.share_type === ShareType.Scheduled;

    return (
      <Paper className={classes.root}>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <Grid container alignItems="center">
              <Grid item>
                <AccessTimeIcon
                  color={isScheduled ? 'primary' : 'disabled'}
                  className={classes.accessIcon}
                />
              </Grid>
              <Grid item>
                <Typography variant="subtitle2">
                  <Trans i18nKey="sharing:scheduler" />
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <ValidatedSwitch
              name="share_type"
              color="primary"
              checkedFn={this.handleCheckedValue}
              onChange={this.handleScheduleChange}
            />
          </Grid>
        </Grid>
        {isScheduled && (
          <Grid container alignItems="center" justify="space-between">
            <Grid item>{this.renderSecondary()}</Grid>
            <Grid item>
              <IconButton onClick={this.handleClick}>
                <MoreHorzIcon />
              </IconButton>
            </Grid>
          </Grid>
        )}
        <DatePickers.DateTime
          enableTime
          value={time}
          anchorEl={anchorEl}
          onClose={this.handleClose}
          onSave={this.handleSave}
        />
      </Paper>
    );
  }
}

export default enhancers(Scheduler);
